<script setup lang="ts">
/**
 * OnlyOffice 全屏编辑台。
 *
 * 编辑器不能挂在抽屉里：打开在线编辑时抽屉要关掉，
 * 挂在里面的 DOM 会跟着一起卸载。所以宿主放在视图层，
 * 抽屉只负责显示服务状态和那个「在线编辑」按钮。
 *
 * 这块原来只有模块中心有；项目页的文件子页看得到版本却编辑不了。
 * 抽成组件后两边各挂一行就都有了。
 */
import { nextTick, onBeforeUnmount, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

const emit = defineEmits<{
  /**
   * 编辑器即将显示。配置和脚本都已经拿到了，这时候外面才关抽屉——
   * 先关再拉配置的话，请求一失败人就白丢一个填了一半的抽屉。
   */
  show: [];
}>();

const visible = ref(false);
const stage = ref<HTMLElement | null>(null);
let docsEditor: { destroyEditor?: () => void } | null = null;

declare global {
  interface Window {
    DocsAPI?: {
      DocEditor: new (elementId: string, config: Record<string, unknown>) => { destroyEditor?: () => void };
    };
  }
}

function loadScript(baseUrl: string): Promise<void> {
  if (window.DocsAPI) return Promise.resolve();
  const source = `${baseUrl.replace(/\/+$/, '')}/web-apps/apps/api/documents/api.js`;
  const existing = document.querySelector<HTMLScriptElement>(`script[data-onlyoffice="${CSS.escape(source)}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('OnlyOffice 编辑器脚本加载失败')), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = source;
    script.dataset.onlyoffice = source;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('OnlyOffice 编辑器脚本加载失败'));
    document.head.append(script);
  });
}

async function open(recordId: string | number): Promise<void> {
  try {
    const payload = await eqsApi.onlyOfficeConfig(recordId);
    await loadScript(payload.documentServerUrl);
    emit('show');
    visible.value = true;
    await nextTick();
    // 编辑台在页面流里，从文件列表点进来时它在视口外——不滚过去的话
    // 人会觉得「点了没反应」，其实编辑器已经在上面加载好了
    stage.value?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    if (!window.DocsAPI) throw new Error('OnlyOffice DocsAPI 未加载');
    docsEditor = new window.DocsAPI.DocEditor('onlyoffice-editor', {
      ...payload.config,
      width: '100%',
      height: '100%',
    });
  } catch (caught) {
    visible.value = false;
    notify('error', 'OnlyOffice 打开失败', errorMessage(caught));
  }
}

function close(): void {
  docsEditor?.destroyEditor?.();
  docsEditor = null;
  visible.value = false;
}

defineExpose({ open, close });
onBeforeUnmount(close);
</script>

<template>
  <section v-if="visible" ref="stage" class="onlyoffice-stage">
    <header>
      <b>OnlyOffice 在线编辑</b>
      <button class="inline-button" type="button" @click="close">关闭编辑器</button>
    </header>
    <div id="onlyoffice-editor"></div>
  </section>
</template>
